import { useEffect, useSyncExternalStore } from 'react';

export type ThemePref = 'light' | 'dark' | 'system';
const KEY = 'planer-theme';
const BG = { light: '#f3f4f6', dark: '#0d0f12' } as const;

function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'dark' || v === 'system' ? v : 'light';
  } catch {
    return 'light';
  }
}

let pref: ThemePref = readPref();
const listeners = new Set<() => void>();
const media = window.matchMedia('(prefers-color-scheme: dark)');

function resolved(p: ThemePref): 'light' | 'dark' {
  return p === 'system' ? (media.matches ? 'dark' : 'light') : p;
}

function apply(): void {
  const mode = resolved(pref);
  document.documentElement.dataset.theme = mode;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', BG[mode]);
}

export function setThemePref(next: ThemePref): void {
  pref = next;
  try {
    localStorage.setItem(KEY, next);
  } catch {
    // private mode: keep it for this session only
  }
  apply();
  listeners.forEach((fn) => {
    fn();
  });
}

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

export function useThemePref(): ThemePref {
  return useSyncExternalStore(subscribe, () => pref);
}

/** Keeps the document in sync with the preference and the OS setting. */
export function useApplyTheme(): void {
  useEffect(() => {
    apply();
    const onChange = () => {
      if (pref === 'system') apply();
    };
    media.addEventListener('change', onChange);
    return () => {
      media.removeEventListener('change', onChange);
    };
  }, []);
}
